import { useQueryClient } from "react-query";
import { getAllPuckPagesQueryKey } from "src/api/client-api/cms/useGetAllPuckPages";
import { CreatePuckPageRequest, PuckPage, useCreatePuckPage } from "src/api/client-api/cms/useCreatePuckPage";
import { useDeletePuckPage } from "src/api/client-api/cms/useDeletePuckPage";
import { useUpdatePuckPage } from "src/api/client-api/cms/useUpdatePuckPage";

export type DuplicatePayload = { title: string; url: string };

type PageContentObject = {
    content?: unknown[];
    root?: {
        props?: Record<string, unknown>;
    };
    roles?: {
        allowedRoles?: string[];
    };
};

const parsePageContent = (page: PuckPage, fallbackTitle = ""): PageContentObject => {
    return page.content ? JSON.parse(page.content) : { content: [], root: { props: { title: fallbackTitle } } };
};

const ensureRootProps = (contentObject: PageContentObject): Record<string, unknown> => {
    contentObject.root = contentObject.root || { props: {} };
    contentObject.root.props = contentObject.root.props || {};
    return contentObject.root.props;
};

const setRootProp = (contentObject: PageContentObject, key: string, value: unknown) => {
    ensureRootProps(contentObject)[key] = value;
};

export const useCMSPagesActions = (
    pages: PuckPage[],
    refetch: () => Promise<unknown> | void,
    pageType: "CMS" | "PDF" = "CMS",
) => {
    const queryClient = useQueryClient();
    const createPageMutation = useCreatePuckPage();
    const deletePageMutation = useDeletePuckPage();
    const updatePageMutation = useUpdatePuckPage();

    const createPage = async (payload: CreatePuckPageRequest) => {
        const result = await createPageMutation.mutateAsync({ ...payload, pageType: payload.pageType ?? pageType });
        queryClient.invalidateQueries(["puckPage", result.slug]);
        queryClient.invalidateQueries([getAllPuckPagesQueryKey]);
        await refetch();
        return result;
    };

    const deletePage = async (slug: string) => {
        await deletePageMutation.mutateAsync({ slug });
        queryClient.invalidateQueries(["puckPage", slug]);
        queryClient.invalidateQueries([getAllPuckPagesQueryKey]);
        await refetch();
    };

    const updateTitle = async (slug: string, title: string) => {
        await updatePageMutation.mutateAsync({ slug, data: { title } });
        // Force remove cached data to ensure fresh fetch
        queryClient.removeQueries([getAllPuckPagesQueryKey]);
        queryClient.invalidateQueries(["puckPage", slug]);
        // Force immediate refetch from server
        await queryClient.refetchQueries([getAllPuckPagesQueryKey], { exact: false });
        await refetch();
    };

    const updateStatus = async (slug: string, status: "active" | "draft") => {
        await updatePageMutation.mutateAsync({ slug, data: { status } });
        queryClient.invalidateQueries([getAllPuckPagesQueryKey]);
        await refetch();
    };

    const updateRoles = async (slug: string, allowedRoles: string[]) => {
        const page = pages.find((p) => p.slug === slug);
        if (!page) return;

        let updatedContent = page.content || "{}";
        try {
            const contentObj = parsePageContent(page, page.title || "");
            contentObj.roles = { allowedRoles };
            updatedContent = JSON.stringify(contentObj);
        } catch (e) {
            console.error("Failed to update roles", e);
        }

        await updatePageMutation.mutateAsync({ slug, data: { content: updatedContent, allowedRoles } });
        queryClient.invalidateQueries([getAllPuckPagesQueryKey]);
        queryClient.invalidateQueries(["puckPage", slug]);
        await refetch();
    };

    const updateMeta = async (
        slug: string,
        payload: {
            url?: string;
            sectors?: string[] | null;
            allowedRoles?: string[];
            status?: "active" | "draft";
            title?: string;
            description?: string;
            downloadOnlyOnSsqValidated?: boolean;
            score?: string | string[];
            company?: string | string[];
            entity?: string | string[];
        },
    ) => {
        const page = pages.find((p) => p.slug === slug);
        let updatedContent: string | undefined = undefined;
        if (page) {
            try {
                const contentObj = parsePageContent(page);
                if (payload.title) {
                    setRootProp(contentObj, "title", payload.title);
                }
                if (payload.description !== undefined) {
                    setRootProp(contentObj, "description", payload.description);
                }
                if (payload.downloadOnlyOnSsqValidated !== undefined) {
                    setRootProp(contentObj, "downloadOnlyOnSsqValidated", payload.downloadOnlyOnSsqValidated);
                }
                if (payload.score !== undefined) {
                    setRootProp(contentObj, "score", payload.score);
                }
                if (payload.entity !== undefined) {
                    const rootProps = ensureRootProps(contentObj);
                    rootProps.entity = payload.entity;
                    delete rootProps.company;
                }
                if (payload.company !== undefined && payload.entity === undefined) {
                    setRootProp(contentObj, "company", payload.company);
                }
                if (payload.allowedRoles) {
                    contentObj.roles = { allowedRoles: payload.allowedRoles };
                }
                updatedContent = JSON.stringify(contentObj);
            } catch (e) {
                console.error("Failed to update meta", e);
            }
        }

        await updatePageMutation.mutateAsync({
            slug,
            data: {
                title: payload.title,
                allowedRoles: payload.allowedRoles,
                status: payload.status,
                url: payload.url,
                sectors: payload.sectors ?? undefined,
                content: updatedContent,
            },
        });
        queryClient.invalidateQueries(["puckPage", slug]);
        queryClient.invalidateQueries([getAllPuckPagesQueryKey]);
        await refetch();
    };

    const duplicatePage = async (source: PuckPage, payload: DuplicatePayload) => {
        const srcContentObj = parsePageContent(source);
        setRootProp(srcContentObj, "title", payload.title);

        if (pageType === "PDF") {
            setRootProp(srcContentObj, "pageType", "PDF");
        } else {
            setRootProp(srcContentObj, "pageType", "CMS");
        }

        const rolesInContent: string[] = srcContentObj.roles?.allowedRoles || [];
        const effectiveRoles: string[] = Array.isArray(source.allowedRoles) ? source.allowedRoles : rolesInContent;
        const sectorsIris: string[] = Array.isArray(source.sectors) ? source.sectors : [];

        const newPage = await createPage({
            title: payload.title,
            content: JSON.stringify(srcContentObj),
            allowedRoles: effectiveRoles,
            url: payload.url,
            sectors: sectorsIris,
            status: "draft",
            pageType,
        });

        return newPage;
    };

    return {
        createPage,
        deletePage,
        updateTitle,
        updateStatus,
        updateRoles,
        updateMeta,
        duplicatePage,
        isCreating: createPageMutation.isLoading,
        isUpdating: updatePageMutation.isLoading,
        isDeleting: deletePageMutation.isLoading,
    };
};
